import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Toast from '../../../../components/Toast';
import { useJobDetailsViewModel } from '../viewModel/useJobDetailsViewModel';
import { Job } from '../../../../pages/company/JobManagement/model/JobModel';
import { formatDate } from '../../../../utils/date'
import { getJobTitleInitials } from '../../../../utils/jobTitleDefault';

const JobDetailsPage: React.FC = () => {
  type JobDetails = Omit<Job, 'benefits' | 'requirements' | 'responsibilities'> & {
    company?: {
      id?: string;
      name?: string;
      description?: string | null;
      website?: string;
      industry?: string;
      company_size?: string;
      logo_url?: string | null;
      founded_year?: number;
      headquarters?: string;
    };
    salary_range?: string;
    published_at?: string;
    deadline?: string;
    benefits?: string | string[] | null;
    requirements?: string | null;
    responsibilities?: string | string[] | null;
  };

  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const {
    state,
    handleApply,
    clearError,
    removeToast,
    clearToasts
  } = useJobDetailsViewModel(jobId);

  // State for active tab
  const [activeTab, setActiveTab] = React.useState<'overview' | 'requirements' | 'company' | 'application'>('overview');

  // Clear toasts when component unmounts
  React.useEffect(() => {
    return () => {
      clearToasts();
    };
  }, []);


  const jobInitials = getJobTitleInitials(state.job?.title || '');

  const formatEnum = (value?: string | null) =>
    value ? value.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase()) : 'N/A';

  const formatTextList = (value?: string | string[] | null) => {
    if (Array.isArray(value)) {
      return value.filter(Boolean);
    }

    return (value || '')
      .split(/\r?\n/)
      .map(item => item.replace(/^[•\-\*]\s*/, '').trim())
      .filter(Boolean);
  };

  const renderTextList = (value?: string | string[] | null, emptyText = 'Not provided.') => {
    const items = formatTextList(value);

    if (items.length === 0) {
      return <div className="text-xs sm:text-sm text-gray-500">{emptyText}</div>;
    }

    return (
      <ul className="space-y-2">
        {items.map((item, index) => (
          <li key={`${item}-${index}`} className="flex items-start gap-2 text-sm text-gray-700">
            <span className="text-indigo-500 mt-1">•</span>
            <span>{item}</span>
          </li>
        ))}
      </ul>
    );
  };

  const renderDetailRow = (label: string, value?: React.ReactNode) => {
    const isEmpty = value === undefined || value === null || value === '';

    return (
      <div className="flex justify-between gap-4 text-xs sm:text-sm">
        <span className="text-gray-500">{label}:</span>
        <span className="font-medium text-gray-900 text-right break-words">{isEmpty ? 'N/A' : value}</span>
      </div>
    );
  };

  if (state.loading) {
    return (
      <div className="flex justify-center items-center min-h-screen p-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 sm:h-12 sm:w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-sm sm:text-base text-gray-600">Loading job details...</p>
        </div>
      </div>
    );
  }

  if (!state.job) {
    return (
      <div className="flex items-center justify-center min-h-screen p-4">
        <div className="text-center max-w-md">
          <div className="text-4xl sm:text-6xl text-gray-400 mb-4">🔍</div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">Job Not Found</h2>
          <p className="text-sm sm:text-base text-gray-600 mb-6">The job you're looking for doesn't exist or has been removed.</p>
          <button
            onClick={() => navigate('/candidate/jobs', { state: { activeTab: 'browse' } })}
            className="px-4 sm:px-6 py-2 bg-indigo-600 text-white text-sm sm:text-base rounded-lg hover:bg-indigo-700 transition-colors w-full sm:w-auto"
          >
            Browse Other Jobs
          </button>
        </div>
      </div>
    );
  }

  const job = state.job as unknown as JobDetails;
  const jobdeadline = job.application_deadline;

  // Tab navigation items
  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'requirements', label: 'Requirements' },
    { id: 'company', label: 'Company' },
  ];

  // Get job type color
  const getJobTypeColor = (type?: string) => {
    switch (type?.toUpperCase()) {
      case 'FULL_TIME': return 'bg-green-100 text-green-800 border border-green-200';
      case 'PART_TIME': return 'bg-blue-100 text-blue-800 border border-blue-200';
      case 'CONTRACT': return 'bg-purple-100 text-purple-800 border border-purple-200';
      case 'INTERNSHIP': return 'bg-yellow-100 text-yellow-800 border border-yellow-200';
      default: return 'bg-gray-100 text-gray-800 border border-gray-200';
    }
  };

  // Get work mode color
  const getWorkModeColor = (mode?: string) => {
    switch (mode?.toUpperCase()) {
      case 'REMOTE': return 'bg-indigo-100 text-indigo-800 border border-indigo-200';
      case 'HYBRID': return 'bg-amber-100 text-amber-800 border border-amber-200';
      case 'ON_SITE': return 'bg-cyan-100 text-cyan-800 border border-cyan-200';
      default: return 'bg-gray-100 text-gray-800 border border-gray-200';
    }
  };

  // Render content based on active tab
  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <div className="space-y-4 sm:space-y-6">
            <div>
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2 sm:mb-3">Job Description</h3>
              <div className="bg-gray-50 rounded-lg p-3 sm:p-4 border border-gray-200 max-h-40 sm:max-h-60 overflow-y-auto">
                <p className="text-xs sm:text-sm text-gray-700 whitespace-pre-line leading-relaxed">
                  {job.description || 'No description provided.'}
                </p>
              </div>
            </div>

            {/* Quick Info Grid */}
            <div className="grid grid-cols-2 gap-2 sm:gap-3">
              <div className="bg-white border border-gray-200 rounded-lg p-2 sm:p-3">
                <div className="text-xs text-gray-500 mb-1">Job Type</div>
                <div className="font-medium text-xs sm:text-sm text-gray-900 truncate">{job.job_type?.replace('_', ' ') || 'N/A'}</div>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-2 sm:p-3">
                <div className="text-xs text-gray-500 mb-1">Experience</div>
                <div className="font-medium text-xs sm:text-sm text-gray-900 truncate">{job.experience_level || 'N/A'}</div>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-2 sm:p-3">
                <div className="text-xs text-gray-500 mb-1">Work Mode</div>
                <div className="font-medium text-xs sm:text-sm text-gray-900 truncate">{job.work_mode?.replace('_', ' ') || 'N/A'}</div>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-2 sm:p-3">
                <div className="text-xs text-gray-500 mb-1">Vacancies</div>
                <div className="font-medium text-xs sm:text-sm text-gray-900">{job.vacancies || 'N/A'}</div>
              </div>
            </div>

            {/* Salary */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4">
              <div className="text-xs sm:text-sm font-medium text-gray-700 mb-1">Salary Range</div>
              <div className="text-base sm:text-lg font-bold text-gray-900">
                {job.salary_range
                  ? `${job.salary_currency || ''} ${job.salary_range}`
                  : 'Not disclosed'}
              </div>
            </div>

            <div>
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2 sm:mb-3">Responsibilities</h3>
              <div className="bg-white border border-gray-200 rounded-lg p-3 sm:p-4">
                {renderTextList(job.responsibilities, 'No responsibilities provided.')}
              </div>
            </div>

            <div>
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2 sm:mb-3">Benefits</h3>
              <div className="bg-white border border-gray-200 rounded-lg p-3 sm:p-4">
                {renderTextList(job.benefits, 'No benefits provided.')}
              </div>
            </div>
          </div>
        );

      case 'requirements':
        return (
          <div className="space-y-4 sm:space-y-6">
            <div>
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2 sm:mb-3">
                Requirements
              </h3>

              {job.requirements ? (
                <ul className="space-y-2">
                  {job.requirements.split('\n').map((req: string, index: number) => (
                    <li
                      key={index}
                      className="flex items-start gap-2 text-sm text-gray-700"
                    >
                      <span className="text-indigo-500 mt-1">•</span>
                      <span>{req.replace(/^•\s*/, '')}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="text-xs sm:text-sm text-gray-500">
                  No requirements provided.
                </div>
              )}
            </div>

            <div>
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2 sm:mb-3">
                Skills
              </h3>

              {job.skills?.length ? (
                <div className="flex flex-wrap gap-2">
                  {job.skills.map((skill: any, index: number) => {
                    const skillName = skill.skill_name || skill.name || String(skill);
                    const meta = [
                      skill.minimum_years ? `${skill.minimum_years}+ yrs` : null,
                      skill.proficiency_level ? formatEnum(skill.proficiency_level) : null,
                      skill.is_required ? 'Required' : null,
                    ].filter(Boolean).join(' • ');

                    return (
                      <div key={skill.id || skill.skill_id || `${skillName}-${index}`} className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
                        <div className="text-sm font-medium text-gray-900">{skillName}</div>
                        {meta && <div className="text-xs text-gray-500 mt-0.5">{meta}</div>}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-xs sm:text-sm text-gray-500">
                  No skills provided.
                </div>
              )}
            </div>

            <div>
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2 sm:mb-3">
                Qualifications
              </h3>
              <div className="bg-white border border-gray-200 rounded-lg p-3 sm:p-4">
                <div className="space-y-2 sm:space-y-3">
                  {renderDetailRow('Education Level', formatEnum(job.education_level))}
                  {renderDetailRow('Job Function', formatEnum(job.job_function))}
                </div>
              </div>
            </div>
          </div>
        );
      case 'company':
        return (
          <div className="space-y-4 sm:space-y-6">
            <div>
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2 sm:mb-3">Company Information</h3>
              <div className="bg-white border border-gray-200 rounded-lg p-3 sm:p-4">
                <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
                  {/* Company Logo or Initials */}
                  {job.company?.logo_url ? (
                    <img
                      src={job.company.logo_url}
                      alt={`${job.company?.name} logo`}
                      className="w-8 h-8 sm:w-12 sm:h-12 rounded-lg object-cover border border-gray-200"
                    />
                  ) : (
                    <div className="w-8 h-8 sm:w-12 sm:h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-bold text-sm sm:text-lg">{jobInitials}</span>
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <h4 className="font-semibold text-sm sm:text-base text-gray-900 truncate">{job.company?.name || 'Company'}</h4>
                    {job.company?.website && (
                      <a
                        href={job.company.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs sm:text-sm text-indigo-600 hover:text-indigo-800 truncate block"
                      >
                        {job.company.website.replace(/^https?:\/\//, '')}
                      </a>
                    )}
                  </div>
                </div>

                {job.company?.description && (
                  <div className="text-xs sm:text-sm text-gray-600 mb-3 sm:mb-4 max-h-20 sm:max-h-24 overflow-y-auto">
                    {job.company.description}
                  </div>
                )}

                <div className="space-y-1.5 sm:space-y-2">
                  <div className="flex justify-between text-xs sm:text-sm">
                    <span className="text-gray-500">Industry:</span>
                    <span className="font-medium text-gray-900 text-right truncate ml-2">{job.company?.industry || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between text-xs sm:text-sm">
                    <span className="text-gray-500">Company Size:</span>
                    <span className="font-medium text-gray-900 text-right">{job.company?.company_size || 'N/A'}</span>
                  </div>
                  {job.company?.founded_year && (
                    <div className="flex justify-between text-xs sm:text-sm">
                      <span className="text-gray-500">Founded:</span>
                      <span className="font-medium text-gray-900 text-right">{job.company.founded_year}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2 sm:mb-3">Job Details</h3>
              <div className="bg-white border border-gray-200 rounded-lg p-3 sm:p-4">
                <div className="space-y-2 sm:space-y-3">
                  {renderDetailRow('Posted', job.published_at ? formatDate(job.published_at) : 'N/A')}
                  {job.department && (
                    renderDetailRow('Department', job.department)
                  )}
                  {jobdeadline && (
                    renderDetailRow('Deadline', formatDate(jobdeadline))
                  )}
                  {renderDetailRow('Target Start Date', job.target_start_date ? formatDate(job.target_start_date) : 'N/A')}
                  {renderDetailRow('Hiring Urgency', formatEnum(job.hiring_urgency))}
                  {job.visa_sponsorship === true && renderDetailRow('Visa Sponsorship', 'Yes')}
                  {job.relocation_offered === true && renderDetailRow('Relocation Offered', 'Yes')}
                  {renderDetailRow('Travel', `${job.travel_percentage || 0}%`)}
                  {renderDetailRow('Interview Process', job.interview_process || 'N/A')}
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <>
      {/* Toast Container - Adjusted for mobile */}
      <div className="fixed top-2 right-2 sm:top-4 sm:right-4 z-50 flex flex-col items-end space-y-2 max-w-[90vw] sm:max-w-md">
        {state.toasts.map(toast => (
          <Toast
            key={toast.id}
            type={toast.type}
            message={toast.message}
            onClose={() => removeToast(toast.id)}
            isVisible={true}
          />
        ))}
      </div>

      <div className="px-3 sm:px-6 py-4 max-w-7xl mx-auto">
        <div className="max-w-6xl mx-auto px-3 sm:px-4 py-3 sm:py-4">
          {/* Back Button */}
          <div className="mb-3 sm:mb-4">
            <button
              onClick={() => navigate('/candidate/jobs', { state: { activeTab: 'browse' } })}
              className="flex items-center gap-1 sm:gap-2 text-gray-600 hover:text-gray-900 text-xs sm:text-sm"
            >
              <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Back to Jobs
            </button>
          </div>

          <div className="bg-white rounded-lg shadow-sm border p-4 sm:p-6 mb-6">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
              <div className="flex items-center">
                {/* Logo */}
                <div className="flex-shrink-0 h-20 w-20">
                  {job.company?.logo_url ? (
                    <img
                      src={job.company.logo_url}
                      alt="company logo"
                      className="h-20 w-20 rounded-full object-cover border"
                    />
                  ) : (
                    <div className="h-20 w-20 rounded-full bg-indigo-100 flex items-center justify-center">
                      <span className="text-indigo-600 font-bold text-2xl">
                        {jobInitials}
                      </span>
                    </div>
                  )}
                </div>

                <div className="ml-6">
                  <h1 className="text-2xl font-bold text-gray-900">
                    {job.title}
                  </h1>

                  <p className="text-lg text-gray-600 mt-1">
                    <span className="font-semibold">
                      {job.company?.name || "Company"}
                    </span>
                  </p>

                  <div className="flex items-center mt-2 space-x-4">
                    {job.location && (
                      <div className="flex items-center gap-1">
                        <span className="text-sm text-gray-500">
                          {job.location}
                        </span>
                      </div>
                    )}

                    {job.experience_level && (
                      <div className="flex items-center gap-1">
                        <span className="text-sm text-gray-500">
                          {job.experience_level}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Apply Button */}
              <div>
                <button
                  onClick={handleApply}
                  disabled={(job as any)?.has_applied || state.applying}
                  className={`px-6 py-2 rounded-md text-sm font-medium ${(job as any)?.has_applied
                    ? "bg-green-100 text-green-800 border border-green-200 cursor-not-allowed"
                    : "bg-indigo-600 text-white hover:bg-indigo-700"
                    }`}
                >
                  {state.applying
                    ? "Applying..."
                    : (job as any)?.has_applied
                      ? "✓ Applied"
                      : "Apply Now"}
                </button>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border">
            <div className="border-b border-gray-200">
              <nav className="-mb-px flex space-x-6 px-4 sm:px-6 overflow-x-auto whitespace-nowrap">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id as any)}
                    className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === tab.id
                      ? "border-indigo-500 text-indigo-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                      }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </nav>
            </div>

            <div className="p-6">
              {renderTabContent()}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default JobDetailsPage;
